import type { HeroProps } from '@/components/home/Hero';
import type { SwitchingEuropeProps } from '@/components/home/SwitchingEurope';
import type {
  CustomersReviewsProps,
  GenericProductsProps,
  HowItWorksProps,
  InstantEverythingProps,
  ProductsProps,
  TrustProps,
} from '@/components/shared';
import type { BasePageConfig } from '@/pageConfig/BasePageConfig';
import { sharedConfig } from '@/pageConfig/shared';
import aRated from '@/static/assets/home/a-rated.png';
import bCorp from '@/static/assets/home/b-corp.png';
import roomLogo from '@/static/assets/home/fr-products-logo-2024.svg';
import room from '@/static/assets/home/living-room-with-checkmarks.svg';
import ogImage from '@/static/assets/home/og-fr.png';
import pizzaDesktop from '@/static/assets/home/pizza_desktop_fr-FR.svg';
import pizzaMobile1 from '@/static/assets/home/pizza_mobile_1.svg';
import pizzaMobile2 from '@/static/assets/home/pizza_mobile_2.svg';
import pizzaMobile3 from '@/static/assets/home/pizza_mobile_3.svg';
import chatFrDesktopLottieConfig from '@/static/genericProducts/chatFrDesktopLottie.json';
import chatFrLottieConfig from '@/static/genericProducts/chatFrLottie.json';
import mapDesktopLottieConfig from '@/static/genericProducts/mapDesktopLottie.json';
import mapLottieConfig from '@/static/genericProducts/mapLottie.json';
import successDesktopLottieConfig from '@/static/genericProducts/successDesktopLottie.json';
import successLottieConfig from '@/static/genericProducts/successLottie.json';

const pizza = {
  desktop: pizzaDesktop,
  mobile: {
    1: pizzaMobile1,
    2: pizzaMobile2,
    3: pizzaMobile3,
  },
};

export interface Config extends BasePageConfig {
  hero: HeroProps;
  customersReviews: CustomersReviewsProps;
  genericProducts: GenericProductsProps;
  instantEverything: InstantEverythingProps;
  howItWorks: HowItWorksProps;
  trust: TrustProps;
  switchingEurope: SwitchingEuropeProps;
  products: ProductsProps;
}

// ORDER HERE MATTERS
export const config: Config = {
  seo: { ...sharedConfig['fr-FR'].seo, ogImage: ogImage.src },
  layout: {
    ...sharedConfig['fr-FR'].layout,
    showAskUsAnything: true,
    alignLogoOnMobile: 'left',
    headerActions: {
      ...sharedConfig['fr-FR'].layout.headerActions,
      CTAs: {
        ...sharedConfig['fr-FR'].layout.headerActions.CTAs,
        checkOurPrices: {
          ...sharedConfig['fr-FR'].layout.headerActions.CTAs.checkOurPrices,
          short: {
            singular: 'Consultez nos prix',
            plural: 'Consultez nos prix',
          },
        },
      },
    },
  },
  hero: {
    heroType: 'frHero',
    title: 'Oubliez tout ce que vous pensez\nsavoir sur les assurances',
    subtitle: 'Rapide. Petits prix. Grand ❤️',
    ctaLabel: 'Consultez nos prix',
    secondaryCta: {
      label: 'Déjà assuré(e) ? Nous résilions votre ancien contrat pour vous',
      scrollToSection: 'switching',
    },
    cart: {
      singular: 'Devis',
      plural: 'Retour à mon devis',
    },
  },
  customersReviews: {
    layout: {
      mt: [-30, 0],
    },
    title: 'Plus de 2 000 000 clients satisfaits !',
    subtitle: `Lemonade est notée 4,9 étoiles sur l’App Store, et\nclassée parmi les meilleures assurances par <a href="https://fr.trustpilot.com/review/lemonade.com" target="_blank">Trustpilot</a>`,
    thirdTitle:
      'Lemonade est une société cotée en bourse (<b>NYSE : LMND</b>) et\nest réassurée par les groupes les plus fiables',
    darkBackground: true,
  },
  products: {
    title: 'Des prix imbattables. Pas de paperasse.\nRésiliation instantannée.',
    showDiamond: false,
    products: [
      {
        title: 'Locataires',
        subtitle: 'La couverture de vos biens chez vous et à l’extérieur',
        price: 'À PARTIR DE 4 € /MOIS',
        product: 'home_eu_renters',
        ctaLabelDesktop: 'consultez nos prix',
        ctaLabelMobile: 'prix',
        customUrl: '/onboarding/start?f=1&ownership=renter',
        alt: 'assurance habitation locataire',
      },
      {
        title: 'Propriétaires',
        subtitle: 'Protégez vos biens, ainsi que<br/>votre propriété',
        price: 'À PARTIR DE 10 € /MOIS',
        product: 'home_eu_owners',
        ctaLabelDesktop: 'consultez nos prix',
        ctaLabelMobile: 'prix',
        customUrl: '/onboarding/start?f=1&ownership=owner',
        alt: 'assurance habitation propriétaire',
      },
      {
        title: 'PNO',
        subtitle: 'Protégez votre logement en tant que propriétaire non-occupant',
        price: 'À PARTIR DE 6 € /MOIS',
        product: 'landlords_eu',
        ctaLabelDesktop: 'consultez nos prix',
        ctaLabelMobile: 'prix',
        customUrl: '/onboarding/start?f=1&ownership=owner',
        alt: 'assurance PNO',
      },
    ],
    trackingLabel: 'home',
    layout: {
      mt: [-10, -50],
    },
  },
  instantEverything: {
    title: 'Tout est fait en temps réel',
    subtitle:
      "Maya, notre charmant chatbot boosté à l'intelligence artificielle, va vous créer la police d'assurance qui vous convient.\nCela n'a jamais été aussi simple et rapide",
    appSketchContentLeft: 'Assuré(e)\nen quelques\n<b>secondes</b>',
    appSketchContentRight: 'Remboursé(e)\nen quelques\n<b>minutes</b>',
    darkBackground: true,
    adjustments: {
      contentSectionLeft: {
        top: { sm: '-15px', md: '-90px' },
        left: { lg: '-425px' },
      },
      contentSectionRight: {
        top: { sm: '40px', md: '-16px' },
        right: { sm: '-178px', lg: '-450px' },
      },
    },
  },
  genericProducts: {
    title: 'Assurance habitation à partir de 4 € pour locataires <br/> et 10€ pour propriétaires',
    subtitle: 'Assurés en quelques secondes. Paiement mensuel. 100% digital',
    bgImage: room.src,
    productLogo: {
      alt: 'Label Excellence 2023',
      img: roomLogo.src,
      width: 95,
      height: 95,
    },
    layout: {
      mt: [-10, -30],
    },
  },
  howItWorks: {
    layout: {
      mt: [-10, -50],
    },
    title: 'Comment ça marche ?',
    subtitle: `Lemonade défie le modèle d'assurance traditionnel. Nous traitons les cotisations que vous payez comme si c'était votre argent, et non le nôtre. Avec Lemonade, tout devient simple et transparent. Nous prélevons des frais fixes, réglons les sinistres très rapidement et <a href="/fr/giveback" aria-labelledby="how-it-works-subtitle">redistribuons</a> l'argent qui reste aux causes qui vous tiennent à cœur`,
    disclaimer: `*Sous réserve de la discrétion du conseil d'administration et de la conformité de la société à certaines normes financières. <a href="/fr/faq#giveback">En savoir plus</a>`,
    pizzaDesktop: pizza.desktop.src,
    pizzaMobile: [
      {
        image: pizza.mobile[1].src,
        text: `Des <b>frais fixes</b> pour<br />s'occuper de tout`,
      },
      {
        image: pizza.mobile[2].src,
        text: 'Votre cotisation sert <br/> à rembourser vos <br/> sinistres <b>rapidement</b>',
      },
      {
        image: pizza.mobile[3].src,
        text: `S'il reste de l'argent,<br/> nous <b>le reversons à<br /> des associations</b>`,
      },
    ],
    showBrandsImage: false,
    darkBackground: true,
  },
  trust: {
    bulletsLayout: 'horizontal',
    darkBackground: true,
    showTopBorder: true,
    bullets: [
      {
        name: 'social',
        title: `Un modèle basé sur l'impact social`,
        text: `Lemonade Inc. est une société d'interêt public et certifiée B-Corp. L'impact social fait partie de notre mission et de notre business model - ce n'est pas juste du marketing`,
        image: {
          src: bCorp,
          width: 103,
          height: 88,
        },
      },
      {
        name: 'rating',
        title: `Notée A et soutenue par des leaders de l'assurance`,
        text: 'Lemonade est classée A-Exceptionnel, entièrement licenciée et réassurée par les groupes les plus fiables',
        image: {
          src: aRated,
          width: 100,
          height: 89,
        },
      },
    ],
  },
  switchingEurope: {
    title: 'Déjà assuré(e) ?\nChangez pour Lemonade instantanément',
    ctaLabel: "CHANGEZ D'ASSURANCE",
    leftLottieConfig: chatFrLottieConfig,
    centerLottieConfig: mapLottieConfig,
    rightLottieConfig: successLottieConfig,
    // desktop
    leftDesktopLottieConfig: chatFrDesktopLottieConfig,
    centerDesktopLottieConfig: mapDesktopLottieConfig,
    rightDesktopLottieConfig: successDesktopLottieConfig,
    steps: [
      'Souscrivez votre\ncontrat Lemonade',
      'Partagez les détails\nde votre ancien contrat',
      `Nous nous occuperons\nde la <a aria-label="Nous nous occuperons de la résiliation pour vous" href="https://www.lemonade.com/fr/blog/comment-resilier-votre-assurance-habitation/" target="_blank">résiliation</a>\npour vous`,
    ],
  },
};
